import { useMemo } from 'react';

export const DOTS = '...';

interface UsePaginationProps {
  totalPages: number;
  siblingCount?: number;
  boundaryCount?: number;
  currentPage: number;
}

const range = (start: number, end: number): number[] => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

export function usePagination({
  totalPages,
  siblingCount = 1,
  boundaryCount = 1,
  currentPage,
}: UsePaginationProps): (number | string)[] {
  return useMemo(() => {
    const totalPageNumbers = boundaryCount * 2 + siblingCount * 2 + 2;

    if (totalPages <= totalPageNumbers) {
      return range(0, totalPages - 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, boundaryCount);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages - 1 - boundaryCount);

    const shouldShowLeftDots = leftSiblingIndex > boundaryCount;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1 - boundaryCount;

    const firstPageIndex = 0;
    const lastPageIndex = totalPages - 1;

    const startPages = range(firstPageIndex, Math.min(boundaryCount - 1, totalPages - 1));
    const endPages = range(Math.max(totalPages - boundaryCount, 0), lastPageIndex);

    if (!shouldShowLeftDots && shouldShowRightDots) {
      let leftItemCount = boundaryCount + 2 * siblingCount + 1;
      if (boundaryCount === 0) {
        leftItemCount = 2 * siblingCount + 1;
      }

      const leftRange = range(0, leftItemCount - 1);
      return [...leftRange, DOTS, ...endPages.slice(Math.max(0, endPages.length - boundaryCount))];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      let rightItemCount = boundaryCount + 2 * siblingCount + 1;
      if (boundaryCount === 0) {
        rightItemCount = 2 * siblingCount + 1;
      }

      const rightRangeStart = Math.max(0, totalPages - rightItemCount);
      const rightRange = range(rightRangeStart, totalPages - 1);
      return [...startPages.slice(0, boundaryCount), DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [...startPages, DOTS, ...middleRange, DOTS, ...endPages];
    }

    return range(0, totalPages - 1);
  }, [totalPages, siblingCount, boundaryCount, currentPage]);
}
